/**
 * The chart, with the AI annotation layer.
 *
 * Built on Lightweight Charts (Apache 2.0), which is TradingView's own renderer
 * and is free for commercial use in a paid product — unlike Advanced Charts.
 * The annotation layer is why we own the render path: it needs frame-accurate
 * access to the coordinate transform, which `priceToCoordinate` provides.
 *
 * Every colour here comes from a CSS custom property. The chart library needs
 * literal values, so they are READ from the token layer at mount rather than
 * hard-coded — which keeps the redesign pass working on a single file.
 */
import { useEffect, useRef, useState } from 'react';
import type { CrosshairBus } from '../lib/crosshair.js';
import type { JSX, ReactNode } from 'react';
import {
  AreaSeries, BarSeries, CandlestickSeries, createChart, createSeriesMarkers, HistogramSeries,
  LineSeries, LineStyle,
  type BarData, type CandlestickData, type HistogramData, type IChartApi,
  type ISeriesApi, type IPriceLine, type LineData, type Time,
} from 'lightweight-charts';
import type { CrosshairTarget, Viewport } from '@aegis/core';
import { chart as chartTokens, chartStyleShape, type ChartStyle } from '@aegis/ui';
import { metric, onThemeChange, token } from '../lib/theme.js';
import type { Analysis, Bar, Instrument, Trade } from '../lib/api.js';
import { delayLabel } from '@aegis/api-client';
import { useIsMobile } from '../lib/viewport.js';
import { SCRIPT_PALETTE, type StudyPlot } from '../lib/studies.js';

/**
 * Everything about the chart's appearance that comes from the token layer.
 *
 * Kept as one function rather than inlined into `createChart`, because it is
 * applied twice: once at creation and again on every theme change. Two copies
 * of this object would drift, and the drift would only show after a theme
 * switch — the least likely thing to be tested by hand.
 *
 * The literals that remain are structural, not visual: `rightOffset` is how
 * much empty space sits to the right of the last bar, `scaleMargins` is
 * headroom, `minMove` is the tick size. None of them is a colour or a font, so
 * none belongs in the token layer.
 */
function chartTheme() {
  return {
    layout: {
      background: { color: token('surfaceBase') },
      textColor: token('textMuted'),
      fontSize: 11,
      fontFamily: metric('--font-mono', 'monospace'),
      panes: { separatorColor: token('lineDefault') },
      /**
       * ATTRIBUTION DISABLED — a deliberate decision, not an oversight.
       *
       * Lightweight Charts defaults this to true, and TradingView asks that the
       * attribution stay visible as a condition of using the library for free.
       * Turning it off was an explicit product call by the operator, recorded
       * here so nobody later "fixes" it as a bug or assumes it was accidental.
       *
       * What is actually at risk: TradingView's remedy is withdrawal of
       * permission to use the library, not a penalty. The chart IS this
       * library, so the downside is losing the renderer rather than paying a
       * fine. See docs/adr/0002-chart-engine.md.
       *
       * The clean resolution is the commercial Advanced Charts licence that
       * ADR-0002 has been pending a quote on since it was written — commercial
       * terms permit removing the attribution outright. Until that lands this
       * flag is carrying a risk the operator has chosen to carry.
       */
      attributionLogo: false,
    },
    grid: {
      vertLines: { color: token('lineSubtle') },
      horzLines: { color: token('lineSubtle') },
    },
    rightPriceScale: { borderColor: token('lineDefault') },
    timeScale: { borderColor: token('lineDefault') },
    crosshair: {
      vertLine: { color: token('textFaint'), style: LineStyle.Dashed, width: 1 as const },
      horzLine: { color: token('textFaint'), style: LineStyle.Dashed, width: 1 as const },
    },
  };
}

/**
 * Series colours for a style.
 *
 * A hollow candle is a filled candle whose up BODY is transparent — Lightweight
 * Charts has no hollow mode, and the border and wick colours are what keep the
 * shape readable once the fill is gone. Drawing it any other way would mean
 * hand-painting bodies over the library's own, which is how the two get out of
 * step by a pixel at some zoom levels.
 *
 * `bars` and `line` take the same up/down pair; the series type does the rest.
 */
function seriesTheme(style: ChartStyle) {
  const up = token('marketUp');
  const down = token('marketDown');
  const shape = chartStyleShape(style);

  if (shape.kind === 'line') {
    // One colour, because a line has no direction at a point — colouring
    // segments by the last close produces a stripe that means nothing.
    return { color: token('accent'), lineWidth: 2 as const };
  }
  if (shape.kind === 'bar') {
    return { upColor: up, downColor: down, thinBars: false };
  }
  return {
    // Transparent rather than the surface colour: a body painted in the page's
    // background hides the wick behind it, and on a long wick that reads as a
    // gap in the data.
    upColor: shape.fillUp ? up : 'transparent',
    downColor: shape.fillDown ? down : 'transparent',
    borderUpColor: up, borderDownColor: down,
    wickUpColor: up, wickDownColor: down,
  };
}

export interface ChartProps {
  symbol: string;
  /**
   * Which chart instance this is, when it is in a workspace.
   *
   * Absent on the mobile-web fallback layout, which has exactly one chart and
   * therefore nothing to sync with. Present means "join the crosshair bus
   * under this id" — see `lib/crosshair.ts`.
   */
  paneId?: string;
  /**
   * Where a crosshair move goes. Empty for a chart in no sync group.
   *
   * Passed in rather than derived here: which panes are grouped is a fact
   * about the workspace, and a chart has no idea one exists.
   */
  crosshairTargets?: readonly CrosshairTarget[];
  /** The shared bus. Absent outside a workspace. */
  crosshair?: CrosshairBus;
  /**
   * The contract being drawn, from the instrument master.
   *
   * Carries tick size and display precision, which decide how the price axis
   * increments and how many decimals it shows. Null while the master is still
   * loading — the fallback below is E-mini S&P's, and is wrong for most
   * contracts, so it must never become permanent.
   */
  instrument: Instrument | null;
  timeframe: string;
  /**
   * How the series is drawn, from the trader's own preference.
   *
   * Not a display nicety: `hollow` and `bars` are what a trader used to Sierra
   * or a DOM-first platform reads without having to translate, and `line` is
   * how a swing trader looks at a daily. The setting existed and was stored
   * long before anything read it — see `packages/ui/src/chartStyle.ts`.
   */
  style: ChartStyle;
  bars: Bar[];
  analysis: Analysis | null;
  trades: Trade[];
  showAnnotations: boolean;
  /** 0 for real-time. Drives the staleness badge from the data itself. */
  delayedByMs: number;
  /**
   * Receives the chart's coordinate transform whenever it changes.
   *
   * How the drawing layer stays pinned to the same pixels as the candles: it
   * does not compute its own scales, it borrows these.
   */
  onViewport?: (v: Viewport) => void;
  /**
   * Custom indicator output, already run and validated.
   *
   * Passed in rather than computed here. A chart that ran scripts would tie
   * the sandbox's lifetime to a component that is rebuilt whenever the style
   * changes, and would run every script twice in a workspace holding two
   * charts of the same symbol.
   */
  studies?: readonly StudyPlot[];
  /** Overlays that need the chart's own stacking context — the drawing layer. */
  children?: ReactNode;
}

/** Height of the volume pane, when the series has volume to draw in it. */
const VOLUME_PANE_PX = 80;

/**
 * Height of one custom study's own pane.
 *
 * Taller than the volume band: an oscillator is read for its SHAPE, and eighty
 * pixels of a bounded series is a flat line with wiggles.
 */
const STUDY_PANE_PX = 110;

/** The first pane index a study may occupy. 0 is price, 1 is volume. */
const FIRST_STUDY_PANE = 2;

/**
 * A stable empty default.
 *
 * A fresh `[]` in the parameter list would be a new array on every render, and
 * the studies effect below would rebuild every series each time.
 */
const EMPTY_STUDIES: readonly StudyPlot[] = [];

export function Chart({
  // `timeframe` was declared and never read. It matters now: a timeframe change
  // is a NEW SERIES, and the data effect has to know that to reframe rather than
  // try to append 5-minute bars onto 1-minute ones.
  symbol, instrument, timeframe, style, bars, analysis, trades, showAnnotations,
  // Declared in `ChartProps` since the beginning and missing from this list
  // until 2026-08-31, which is how the staleness badge went unbuilt without
  // anything reporting it: every caller passed the value in correctly and the
  // component never took delivery. TypeScript cannot catch that — an unread
  // prop is legal, and the type only describes what may be passed.
  delayedByMs,
  onViewport, children, paneId, crosshairTargets: targets, crosshair,
  studies = EMPTY_STUDIES,
}: ChartProps): JSX.Element {
  const isMobile = useIsMobile();
  const holder = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  /**
   * The price series, whichever kind it currently is.
   *
   * Lightweight Charts cannot change a series' type in place, so a style change
   * rebuilds it — which is why the create effect below depends on `style` and
   * why everything downstream reads this ref rather than closing over a series.
   */
  const candlesRef = useRef<
    ISeriesApi<'Candlestick'> | ISeriesApi<'Bar'> | ISeriesApi<'Line'> | null
  >(null);
  const volumeRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  /**
   * One series per study plot, keyed by `scriptId:plotId`.
   *
   * Held in a ref rather than in state: these are library handles, not data,
   * and re-rendering because a series was created would rebuild the chart that
   * owns it.
   */
  const studyRef = useRef(new Map<string, ISeriesApi<'Line' | 'Histogram' | 'Area'>>());
  /** What the study series were last built FOR, so a rebuild can be detected. */
  const studyLayoutRef = useRef('');
  /** The theme the study colours were captured at. Canvas does not follow CSS. */
  const drawnStudyThemeRef = useRef(-1);
  /**
   * The last close drawn, for a crosshair arriving with no price of its own.
   *
   * A time-only position still has to be given SOME price — the library takes
   * both — and zero would pin the horizontal line to the bottom of the scale,
   * which reads as a level at zero. This puts it somewhere unremarkable while
   * the vertical line, the part that carries the meaning, lands correctly.
   */
  const lastPriceRef = useRef<number | null>(null);
  const linesRef = useRef<IPriceLine[]>([]);

  /**
   * What was last drawn, so an update can tell a NEW SERIES from a NEW BAR.
   *
   * This is the whole fix for a chart that would not stay where you put it.
   * `bars` is a new array on every tick, and the effect below used to answer
   * that with `setData()` + `fitContent()` — replacing the series and snapping
   * the viewport back to "all of it", several times a minute. Zoom in, pan, and
   * the next tick threw it away. It read as a glitch; it was the render path.
   */
  const drawnRef = useRef<{ key: string; first: number; count: number }>({
    key: '', first: 0, count: 0,
  });
  const [zone, setZone] = useState<{ top: number; height: number; label: string } | null>(null);
  // Bumped on a theme change, so the effects that bake colours into DATA —
  // volume bar tints, annotation lines — re-run and pick up the new tokens.
  const [themeVersion, setThemeVersion] = useState(0);

  // --- create ---------------------------------------------------------------
  useEffect(() => {
    if (!holder.current) return;

    const chart = createChart(holder.current, {
      autoSize: true,
      ...chartTheme(),
      timeScale: {
        ...chartTheme().timeScale,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: chartTokens.rightOffsetBars,
      },
      rightPriceScale: {
        ...chartTheme().rightPriceScale,
        scaleMargins: { top: chartTokens.priceScalePadding, bottom: chartTokens.priceScalePadding },
      },
    });

    const shape = chartStyleShape(style);
    const SeriesType = shape.kind === 'line' ? LineSeries
      : shape.kind === 'bar' ? BarSeries
        : CandlestickSeries;

    const candles = chart.addSeries(SeriesType as typeof CandlestickSeries, {
      ...(seriesTheme(style) as object),
      /**
       * The last-price line and its axis label.
       *
       * Both were off, so the chart showed where price had BEEN and never a
       * marker for where it is. Lightweight Charts is TradingView's own
       * library and draws exactly the line TradingView does — across the plot,
       * coloured by the forming candle, with the price on the axis — so this
       * is two flags rather than a hand-drawn overlay.
       *
       * It follows the series' last value, which is why the tick merge in
       * App.tsx matters: without it the series only ever moved on a completed
       * bar, and the line would have marked a close up to a timeframe old
       * while claiming to be current.
       */
      lastValueVisible: true,
      priceLineVisible: true,
      priceLineStyle: LineStyle.Dotted,
      /**
       * From the instrument master, never hard-coded — CLAUDE.md.
       *
       * `minMove` is the smallest increment the axis may show. Hard-coded at
       * 0.25 it was correct for ES and MES and wrong for the other fourteen
       * listed contracts: crude oil ticks at 0.01, natural gas at 0.001, and
       * the Dow at a whole point with no decimals at all.
       */
      priceFormat: {
        type: 'price',
        precision: instrument?.displayPrecision ?? 2,
        minMove: instrument ? Number(instrument.tickSize) : 0.25,
      },
    });

    const volume = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'vol',
    }, 1);
    // Height is set from the data below, not here. Spot FX reports no volume
    // and this pane was reserved for it regardless — 80px of a chart given to
    // an empty band, on the asset class whose price range is narrowest.

    chartRef.current = chart;
    candlesRef.current = candles;
    volumeRef.current = volume;

    /**
     * Re-read the tokens when the theme changes.
     *
     * The chart is canvas, so it does not follow CSS the way every other
     * surface does — its colours are literal strings captured at creation.
     * Without this, switching to light leaves a dark chart sitting on a white
     * page, which is exactly what it did before this existed.
     */
    const unsubscribe = onThemeChange(() => {
      chart.applyOptions(chartTheme());
      candles.applyOptions(seriesTheme(style) as never);
      // Volume bar colours are baked into the data, so the series has to be
      // re-tinted from whatever is currently on screen.
      setThemeVersion((v) => v + 1);
    });

    /*
      Crosshair sync, both directions.

      Registered inside the create effect rather than in one of its own,
      because both halves need the chart and the series instances — and those
      are rebuilt whenever the style changes, which would leave a separate
      effect holding a handle to a removed chart.
    */
    let offBus: (() => void) | null = null;
    if (paneId && crosshair) {
      // Inbound: draw where another chart's pointer is.
      offBus = crosshair.subscribe(paneId, (position) => {
        if (position === null) { chart.clearCrosshairPosition(); return; }
        // `setCrosshairPosition` needs a price even when only time is
        // travelling. Zero would draw the horizontal line at the bottom of the
        // scale, so the series' own last value is used: the vertical line
        // lands where it should and the horizontal one sits somewhere
        // unremarkable rather than somewhere wrong.
        const price = position.price ?? lastPriceRef.current;
        if (price === null) return;
        chart.setCrosshairPosition(price, position.time as never, candles);
      });

      // Outbound.
      chart.subscribeCrosshairMove((param) => {
        if (!targets || targets.length === 0) return;
        if (param.time === undefined) { crosshair.emit(paneId, null, targets); return; }
        const value = param.seriesData.get(candles) as
          { close?: number; value?: number } | undefined;
        crosshair.emit(paneId, {
          time: param.time as unknown as number,
          price: value?.close ?? value?.value ?? null,
        }, targets);
      });
    }

    return () => {
      offBus?.();
      unsubscribe();
      chart.remove();
      chartRef.current = null;
      candlesRef.current = null;
      volumeRef.current = null;
      linesRef.current = [];
    };
    // `style` rebuilds the series, not just its colours — see `candlesRef`.
  }, [symbol, style]);

  // --- data -----------------------------------------------------------------
  /**
   * Three cases, and telling them apart is the difference between a chart you
   * can navigate and one that keeps snapping back.
   *
   *   1. A NEW BAR, or the last one moving. The common case, several times a
   *      minute. `update()` touches only the tail and leaves the viewport
   *      alone, which is exactly what someone who has zoomed in wants.
   *   2. A NEW SERIES — different symbol, different timeframe, or a different
   *      first bar because history was reloaded or replay seeked. `setData()`,
   *      and `fitContent()` is CORRECT here: this is a different chart and
   *      framing it is the helpful thing.
   *   3. THE SAME SERIES, REDRAWN — a theme change recolouring the volume bars.
   *      `setData()` is needed to restyle, but the viewport must survive it, so
   *      the visible range is saved and put back.
   *
   * The previous version did (2) for all three, on every tick.
   */
  useEffect(() => {
    const candles = candlesRef.current;
    const volume = volumeRef.current;
    const chart = chartRef.current;
    if (!candles || !volume || !chart || bars.length === 0) return;

    const up = token('marketUp');
    const down = token('marketDown');

    /**
     * One bar, in whatever shape the current series eats.
     *
     * A line series takes `{time, value}` and would silently draw nothing given
     * OHLC — no error, just an empty chart — which is why the shape is derived
     * from the same descriptor that chose the series type rather than from a
     * second opinion about the style.
     */
    const shape = chartStyleShape(style);
    const pointAt = (b: Bar): CandlestickData | BarData | LineData =>
      (shape.kind === 'line'
        ? { time: b.time as Time, value: b.close }
        : { time: b.time as Time, open: b.open, high: b.high, low: b.low, close: b.close });
    /**
   * Does this series carry volume at all?
   *
   * Read off the BARS rather than off the instrument, because "is there volume
   * here" is a question the data can answer — a venue that starts reporting it
   * needs no client change, and a contract with a volumeless stretch on screen
   * gets its height back for as long as that is true. The phone makes the same
   * call from the same question; see `Chart.tsx` there.
   */
  const volumeAt = (b: Bar): HistogramData => ({
      time: b.time as Time,
      value: b.volume,
      color: b.close >= b.open ? `${up}55` : `${down}55`,
    });

    const key = `${symbol}|${timeframe}`;
    const first = bars[0]!.time;
    const drawn = drawnRef.current;
    const sameSeries = drawn.key === key && drawn.first === first && drawn.count > 0;
    // Growing, or the last bar being revised in place. Anything else — a
    // shorter array, a gap — is not something `update()` can express.
    const appendOnly = sameSeries && bars.length >= drawn.count;

    if (appendOnly) {
      // From the previously last bar, because it may have moved rather than
      // closed. `update()` on an existing timestamp replaces that bar.
      for (let i = Math.max(0, drawn.count - 1); i < bars.length; i++) {
        candles.update(pointAt(bars[i]!) as never);
        volume.update(volumeAt(bars[i]!));
      }
    } else {
      const range = chart.timeScale().getVisibleLogicalRange();

      candles.setData(bars.map(pointAt) as never);
      volume.setData(bars.map(volumeAt));

      if (sameSeries && range) {
        // Same chart, restyled. Put the reader back where they were.
        chart.timeScale().setVisibleLogicalRange(range);
      } else {
        // A genuinely different series. Frame it.
        chart.timeScale().fitContent();
      }
    }

    // Kept for a crosshair that arrives carrying only a time. See the ref.
    lastPriceRef.current = bars.at(-1)?.close ?? null;

    // The pane takes its height from whether there is anything to put in it.
    // Collapsed rather than removed: the series stays alive, so a symbol that
    // does carry volume gets its pane back without rebuilding the chart.
    chart.panes()[1]?.setHeight(bars.some((b) => b.volume > 0) ? VOLUME_PANE_PX : 1);

    drawnRef.current = { key, first, count: bars.length };
  }, [bars, themeVersion, symbol, timeframe, style]);

  // --- custom studies -------------------------------------------------------
  /**
   * Draw the output of the trader's installed scripts.
   *
   * ## Rebuilt on a LAYOUT change, updated on a data change
   *
   * A study's series cannot be moved between panes and cannot change type, so
   * anything that alters which plots exist, what they look like, or which pane
   * they belong to tears the lot down and builds again. A new set of VALUES for
   * the same plots is just `setData`, which is the case that happens twice a
   * second while the chart ticks.
   *
   * ## One pane per non-overlay SCRIPT, not per plot
   *
   * A script drawing two lines means them to be read against each other, so
   * they share a scale. Two different scripts do not: an oscillator bounded to
   * 100 sharing a pane with a volume-weighted figure in the thousands flattens
   * the first into a line along the axis.
   */
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const scriptIds: string[] = [];
    for (const plot of studies) {
      const scriptId = plot.key.slice(0, plot.key.lastIndexOf(':'));
      if (!plot.overlay && !scriptIds.includes(scriptId)) scriptIds.push(scriptId);
    }
    const paneOf = (plot: StudyPlot): number => {
      if (plot.overlay) return 0;
      return FIRST_STUDY_PANE + scriptIds.indexOf(plot.key.slice(0, plot.key.lastIndexOf(':')));
    };

    const layout = studies
      .map((p) => `${p.key}|${p.style}|${p.colorIndex}|${p.lineWidth}|${paneOf(p)}`)
      .join(',');

    if (layout !== studyLayoutRef.current || themeVersion !== drawnStudyThemeRef.current) {
      for (const series of studyRef.current.values()) {
        try { chart.removeSeries(series); } catch { /* chart already disposed */ }
      }
      studyRef.current.clear();

      /* Panes are removed from the top down. Removing index 2 first would
         renumber everything above it, and the next removal would take a pane
         that is no longer the one it named. */
      for (let i = chart.panes().length - 1; i >= FIRST_STUDY_PANE; i -= 1) {
        try { chart.removePane(i); } catch { /* already gone */ }
      }

      for (const plot of studies) {
        const colour = token(SCRIPT_PALETTE[plot.colorIndex] ?? 'accent');
        const pane = paneOf(plot);
        const series = plot.style === 'histogram'
          ? chart.addSeries(HistogramSeries, { color: colour, priceFormat: { type: 'price' } }, pane)
          : plot.style === 'area'
            ? chart.addSeries(AreaSeries, {
              lineColor: colour, topColor: colour, bottomColor: 'transparent',
              lineWidth: plot.lineWidth as 1 | 2 | 3 | 4,
            }, pane)
            : chart.addSeries(LineSeries, {
              color: colour,
              lineWidth: plot.lineWidth as 1 | 2 | 3 | 4,
              lineStyle: LineStyle.Solid,
              /* `dots` is a line with no line — the library has no point
                 series, and drawing one as a two-pixel line with markers is
                 the same picture with a dependency on the markers plugin. */
              lineType: plot.style === 'stepline' ? 1 : 0,
              pointMarkersVisible: plot.style === 'dots',
              ...(plot.style === 'dots' ? { lineVisible: false, pointMarkersRadius: 2 } : {}),
            }, pane);
        studyRef.current.set(plot.key, series as ISeriesApi<'Line' | 'Histogram' | 'Area'>);
      }

      for (let i = 0; i < scriptIds.length; i += 1) {
        chart.panes()[FIRST_STUDY_PANE + i]?.setHeight(STUDY_PANE_PX);
      }

      studyLayoutRef.current = layout;
      drawnStudyThemeRef.current = themeVersion;
    }

    for (const plot of studies) {
      const series = studyRef.current.get(plot.key);
      if (!series) continue;
      /* A gap is a whitespace point — `{ time }` with no value — rather than a
         missing entry. Dropping the bar entirely would make the line join
         across the gap, drawing a value through bars where the script said
         there was none. */
      series.setData(plot.points.map((p) => (
        p.value === null ? { time: p.time as Time } : { time: p.time as Time, value: p.value }
      )) as never);
    }
  }, [studies, themeVersion]);

  // --- journal trade markers ------------------------------------------------
  useEffect(() => {
    const candles = candlesRef.current;
    if (!candles) return;
    // A marker for a trade outside the rendered range gets clamped to the edge
    // by the library, which stacks dozens of labels on top of each other at the
    // chart border. Only plot what is actually on screen.
    if (bars.length === 0) return;
    const from = bars[0]!.time;
    const to = bars[bars.length - 1]!.time;
    const inRange = (iso: string): boolean => {
      const t = Math.floor(new Date(iso).getTime() / 1000);
      return t >= from && t <= to;
    };

    const relevant = trades
      .filter((t) => t.symbol === symbol && inRange(t.openedAt))
      .slice(-40);
    if (relevant.length === 0) return;

    {
      const up = token('marketUp');
      const down = token('marketDown');
      const markers = relevant.flatMap((t) => {
        const entryTime = Math.floor(new Date(t.openedAt).getTime() / 1000) as Time;
        const out = [{
          time: entryTime,
          position: (t.direction === 'long' ? 'belowBar' : 'aboveBar') as 'belowBar' | 'aboveBar',
          color: t.direction === 'long' ? up : down,
          shape: (t.direction === 'long' ? 'arrowUp' : 'arrowDown') as 'arrowUp' | 'arrowDown',
          text: `${t.direction === 'long' ? 'B' : 'S'} ${Number(t.quantity)}`,
        }];
        if (t.closedAt && inRange(t.closedAt)) {
          out.push({
            time: Math.floor(new Date(t.closedAt).getTime() / 1000) as Time,
            position: (t.direction === 'long' ? 'aboveBar' : 'belowBar') as 'belowBar' | 'aboveBar',
            color: token('textMuted'),
            shape: (t.direction === 'long' ? 'arrowDown' : 'arrowUp') as 'arrowUp' | 'arrowDown',
            text: `X ${Number(t.netPnl) >= 0 ? '+' : ''}${Number(t.netPnl).toFixed(0)}`,
          });
        }
        return out;
      }).sort((a, b) => Number(a.time) - Number(b.time));
      createSeriesMarkers(candles, markers);
    }
  }, [trades, symbol, bars.length, themeVersion]);

  // --- AI annotation layer --------------------------------------------------
  useEffect(() => {
    const candles = candlesRef.current;
    if (!candles) return;

    for (const line of linesRef.current) candles.removePriceLine(line);
    linesRef.current = [];
    setZone(null);

    if (!showAnnotations || !analysis) return;

    const ai = token('aiPrimary');
    const muted = token('textMuted');
    const bg = token('surfaceBase');

    for (const a of analysis.annotations) {
      if (a.kind === 'level' && a.price !== undefined) {
        // Session levels are neutral; AI-derived levels carry the AI hue so a
        // user can never mistake the machine's line for their own drawing.
        const isSession = a.evidence.some((e) => e.includes('session'));
        linesRef.current.push(candles.createPriceLine({
          price: a.price,
          color: isSession ? muted : ai,
          lineWidth: 1,
          lineStyle: isSession ? LineStyle.Dotted : LineStyle.Dashed,
          /* Off on a phone for the same reason as the title: seven price
             badges in a 64px scale overlap each other and the scale's own
             ticks. The legend below carries both the label and the price, so
             nothing is lost by keeping the scale readable. */
          axisLabelVisible: !isMobile,
          /* NO TITLE ON A PHONE.
             Lightweight Charts draws a price line's title as a floating label
             on the plot itself, at the line's height. On a desktop that is
             fine — there is width to the left of the price scale for it to sit
             in. At 390pt seven of them overlap each other and the price scale,
             which is the single worst thing on the mobile chart.
             The lines stay. The labels move to the legend below the chart,
             where the same information is readable and in price order. */
          title: isMobile ? '' : `${isSession ? '' : 'AI · '}${a.label}`,
          axisLabelColor: isSession ? muted : ai,
          axisLabelTextColor: bg,
        }));
      }
    }

    // Zones use the coordinate API — the capability the chart choice was made
    // for, and the reason we own the render path rather than licensing one.
    const zoneAnn = analysis.annotations.find(
      (a) => a.kind === 'zone' && a.priceHigh !== undefined && a.priceLow !== undefined,
    );
    if (zoneAnn) {
      const draw = (): void => {
        const top = candles.priceToCoordinate(zoneAnn.priceHigh!);
        const bottom = candles.priceToCoordinate(zoneAnn.priceLow!);
        if (top === null || bottom === null) return;
        setZone({
          top: Math.min(top, bottom),
          height: Math.max(2, Math.abs(bottom - top)),
          label: zoneAnn.label,
        });
      };
      requestAnimationFrame(draw);
      const sub = chartRef.current?.timeScale();
      sub?.subscribeVisibleTimeRangeChange(draw);
      return () => sub?.unsubscribeVisibleTimeRangeChange(draw);
    }
    return undefined;
  }, [analysis, showAnnotations, bars.length, themeVersion, isMobile]);

  /**
   * Publish the coordinate transform so the drawing layer shares it exactly.
   *
   * Rebuilt on every pan, zoom and resize — the functions close over the
   * chart's current state, and a stale viewport would draw a user's trendline
   * where it used to be. `logical` conversions are not used: drawings are
   * anchored in real time and price, so they survive a timeframe change.
   */
  useEffect(() => {
    if (!onViewport) return undefined;

    const publish = (): void => {
      const chart = chartRef.current;
      const series = candlesRef.current;
      const el = holder.current;
      if (!chart || !series || !el) return;

      const ts = chart.timeScale();
      onViewport({
        timeToX: (t) => ts.timeToCoordinate(t as Time),
        priceToY: (p) => series.priceToCoordinate(p),
        xToTime: (x) => {
          const t = ts.coordinateToTime(x);
          return typeof t === 'number' ? t : null;
        },
        yToPrice: (y) => series.coordinateToPrice(y),
        width: el.clientWidth,
        height: el.clientHeight,
      });
    };

    publish();
    const ts = chartRef.current?.timeScale();
    ts?.subscribeVisibleLogicalRangeChange(publish);
    const observer = new ResizeObserver(publish);
    if (holder.current) observer.observe(holder.current);

    return () => {
      ts?.unsubscribeVisibleLogicalRangeChange(publish);
      observer.disconnect();
    };
  }, [onViewport, bars.length, symbol]);

  return (
    <div className="chart-holder">
      <div ref={holder} className="chart-canvas" />
      {children}
      {/*
        The staleness badge, drawn from the data rather than from a flag.

        CLAUDE.md: "Delayed data must never render as real-time. `delayedByMs`
        travels with every tick to the render layer so the staleness badge
        comes from the data itself, not a separate flag that can drift."

        This prop was declared and never read. The badge existed — but only in
        parents that remembered to draw their own (`App.tsx`, `OrderBook.tsx`,
        `Footprint.tsx`), which is precisely the separate flag the rule warns
        about. `TradeDetail.tsx` threaded `delayedByMs` in faithfully and drew
        nothing, so a delayed feed rendered identically to a live one on the
        trade review screen.

        Drawing it here makes the badge a property of any chart, so a new
        surface cannot forget it. Parents that already draw their own are
        unaffected: they pass 0 when the feed is real-time, and where they do
        pass a delay the two agree because both read the same field.
      */}
      {delayedByMs > 0 && (
        <div
          className="chart-delay-badge"
          /* `status` not `alert`: it is a standing condition, not an event,
             and an assertive live region would interrupt a screen reader on
             every redraw. */
          role="status"
          title="These prices are delayed. The delay comes from the feed itself."
        >
          {delayLabel(delayedByMs).toUpperCase()}
        </div>
      )}
      {zone && (
        <>
          <div
            className="chart-zone"
            style={{ top: `${zone.top}px`, height: `${zone.height}px` }}
            aria-hidden="true"
          />
          {/* The band stays; the caption does not. At 390pt this label runs
              most of the width of the plot and sits on the candles. The
              analysis panel directly below says the same thing in full — this
              was always the glanceable duplicate of it. */}
          <div className="chart-zone-label" style={{ top: `${zone.top - 20}px` }} hidden={isMobile}>
            AI · {zone.label}
          </div>
        </>
      )}
    </div>
  );
}
